import { useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'

import { SYSTEM_EXCLUDE_VALUE, filterRows, rankKeys, toOptions, totalsBy } from './activityReport'
import { actionMetaFor, getReport } from './reportRegistry'

export const ACTIVITY_REPORT = gql`query ActivityReport($report: String!, $dateAddedGte: Date!, $dateAddedLte: Date!) {
    activityReport(report: $report, dateAddedGte: $dateAddedGte, dateAddedLte: $dateAddedLte) {
        userId
        userName
        username
        branchName
        action
        eventDate
        objectsCount
        eventsCount
        objectIds
    }
}`

/**
 * Each report's per-viewer scope, keyed by id.
 *
 * Deliberately not `hasPermissions`: that field returns only the codenames the
 * caller asked for, so a component that forgets one gets a silent false negative
 * and its check becomes dead code. Asking the server for the catalog it owns
 * removes the chance to forget.
 *
 * Only `id` and `canViewAllUsers` -- everything else this response could carry
 * (name, subtitle, ...) comes from the local `reportRegistry.js` entry
 * instead, which every caller already has via `getReport(reportId)`.
 */
export const REPORT_CATALOG = gql`query ReportCatalog {
    reportCatalog {
        id
        canViewAllUsers
    }
}`

/**
 * Groups and their members, for the group filter.
 *
 * Uses userGroupMemberships rather than allGroups { userSet }: userSet is a
 * nested connection re-evaluated per group at two queries each, which fired a
 * hundred queries on a few dozen groups. The flat field is two queries however
 * many groups exist.
 *
 * Members are matched to report rows by id, never by name: display names are not
 * unique, and the report's name formula and `UserNode.resolve_name` disagree for
 * a user who has a last name but no first name.
 */
export const ACTIVITY_REPORT_GROUPS = gql`query ActivityReportGroups {
    userGroupMemberships {
        id
        name
        userIds
    }
}`

/**
 * Resolves the display name for one row's three possible actor populations
 * (see the docstring in `activity/query.py`), so every downstream consumer
 * -- charts, tables, filters, the heatmap -- can treat `userName` as always a
 * non-empty string and never has to special-case null again.
 *
 * - `userId === null`: no actor at all (cron, signals, migrations,
 *   integrations) -- labelled "System".
 * - `userId` set but `userName` missing: the id matched no `auth_user` row --
 *   `buggy/middleware.py` logs `request.user.id` into history rows whenever
 *   `request.user` is authenticated, and on `/mobileql` that principal is a
 *   `MobileAppDriver`, whose id is a `cars_driver` pk, not an `auth_user` pk.
 *   These are customers, not staff. Labelled "Customer #<id>", never
 *   "System": this is real, attributable activity, just not a name we have
 *   on file (resolving it to an actual customer name would mean joining
 *   `cars_driver`, which this does not do).
 * - otherwise: the name BigQuery resolved, unchanged.
 */
const resolveUserName = (row) => {
    if (row.userId === null) return { ...row, userName: 'System' }
    if (!row.userName) return { ...row, userName: `Customer #${row.userId}` }
    return row
}

/**
 * Fetches one report for a date range and derives its filter options.
 *
 * The action filter is applied by the caller, not here: it is a display concern
 * and filtering client-side keeps the response instant.
 */
const useActivityReport = ({ reportId, startDate, endDate, filters }) => {
    const { data, loading, error } = useQuery(ACTIVITY_REPORT, {
        variables: { report: reportId, dateAddedGte: startDate, dateAddedLte: endDate },
        skip: !reportId,
    })

    // Neither of these depends on the date range or the report, so they run once
    // and are served from the Apollo cache thereafter.
    const { data: catalogData, loading: catalogLoading } = useQuery(REPORT_CATALOG)
    const { data: groupData } = useQuery(ACTIVITY_REPORT_GROUPS)

    return useMemo(() => {
        const rows = (data?.activityReport || []).map(resolveUserName)
        const catalog = catalogData?.reportCatalog || []
        const actionMeta = actionMetaFor(getReport(reportId))

        const groupOptions = (groupData?.userGroupMemberships || [])
            .map((group) => ({
                value: group.id,
                label: group.name,
                userIds: (group.userIds || []).map(String),
            }))
            // A group with no members would silently filter everything away, so
            // it is not offered as a choice.
            .filter((group) => group.userIds.length)

        // Options come from the *unfiltered* rows, so narrowing a filter never
        // empties the dropdown you are filtering with.
        const userOptions = toOptions(rankKeys(totalsBy(rows, (row) => row.userName, actionMeta)))
        const branchOptions = toOptions(rankKeys(totalsBy(rows, (row) => row.branchName, actionMeta)))

        // The exclude-users filter offers the same people as `userOptions`,
        // plus a "System" entry for rows with no actor at all (`userId ===
        // null`), but its option values are ids, not names: exclusion has to
        // match `filterRows`'s `excludeUserIds`, which is id-based because
        // display names are not unique (see `filterRows`'s docstring). One
        // option per distinct `userId`, so two people sharing a display name
        // still get two separate, individually-excludable entries. System has
        // no real id, so it is keyed by `SYSTEM_EXCLUDE_VALUE`, the sentinel
        // `filterRows` treats a null userId as equivalent to -- ranked
        // alongside everyone else by the same volume this report actually saw.
        const excludeUserKey = (row) => (row.userId === null ? SYSTEM_EXCLUDE_VALUE : row.userId)
        const excludeUserLabelByKey = {}
        rows.forEach((row) => {
            const key = excludeUserKey(row)
            if (!(key in excludeUserLabelByKey)) {
                excludeUserLabelByKey[key] = row.userId === null ? 'System' : row.userName
            }
        })
        const excludeUserOptions = rankKeys(totalsBy(rows, excludeUserKey, actionMeta)).map((key) => ({
            value: String(key),
            label: excludeUserLabelByKey[key],
        }))

        return {
            loading,
            error,
            rows,
            filteredRows: filterRows(rows, filters),
            userOptions,
            branchOptions,
            groupOptions,
            excludeUserOptions,
            catalog,
            // Defaults to false, not true: while the catalog is still loading this
            // is a permission-derived flag with no answer yet, and a self-scoped
            // viewer briefly seeing (then losing) the full multi-user layout is a
            // worse transition than the reverse. Callers that need to distinguish
            // "no" from "not answered yet" should check `catalogLoading` too.
            canViewAllUsers: catalog.find((entry) => entry.id === reportId)?.canViewAllUsers ?? false,
            // Exposed separately from `loading`, which reflects only the
            // ACTIVITY_REPORT query: a consumer choosing the self-scoped vs
            // full layout needs to know the catalog itself is still in flight,
            // rather than trusting the `false` default above.
            catalogLoading,
        }
    }, [data, catalogData, catalogLoading, groupData, loading, error, filters, reportId])
}

export default useActivityReport
